import { useCallback, useEffect, useRef } from "react";

export const LESSON_DND_SCROLL_ATTRIBUTE = "data-lesson-dnd-scroll-container";

const EDGE_SIZE = 72;
const MAX_STEP = 18;

export function scrollStepForPointer(pointerY: number, top: number, bottom: number): number {
  if (pointerY < top + EDGE_SIZE) {
    return -Math.ceil(MAX_STEP * (1 - Math.max(0, pointerY - top) / EDGE_SIZE));
  }
  if (pointerY > bottom - EDGE_SIZE) {
    return Math.ceil(MAX_STEP * (1 - Math.max(0, bottom - pointerY) / EDGE_SIZE));
  }
  return 0;
}

export function findLessonDndScrollContainer(x: number, y: number): HTMLElement | null {
  const containers = Array.from(
    document.querySelectorAll<HTMLElement>(`[${LESSON_DND_SCROLL_ATTRIBUTE}]`),
  );
  return containers.find((element) => {
    const rect = element.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }) || null;
}

export function useLessonDndAutoScroll() {
  const dragging = useRef(false);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const frame = useRef<number | null>(null);

  const tick = useCallback(() => {
    if (!dragging.current) {return;}
    const coordinates = pointer.current;
    const container = coordinates
      ? findLessonDndScrollContainer(coordinates.x, coordinates.y)
      : null;
    if (container && container.scrollHeight > container.clientHeight) {
      const rect = container.getBoundingClientRect();
      const step = scrollStepForPointer(coordinates!.y, rect.top, rect.bottom);
      if (step !== 0) {container.scrollTop += step;}
    }
    frame.current = window.requestAnimationFrame(tick);
  }, []);

  const updatePointer = useCallback((event: PointerEvent | TouchEvent) => {
    const point = "touches" in event ? event.touches[0] : event;
    if (point) {pointer.current = { x: point.clientX, y: point.clientY };}
  }, []);

  const stop = useCallback(() => {
    dragging.current = false;
    pointer.current = null;
    if (frame.current !== null) {window.cancelAnimationFrame(frame.current);}
    frame.current = null;
    window.removeEventListener("pointermove", updatePointer);
    window.removeEventListener("touchmove", updatePointer);
  }, [updatePointer]);

  const start = useCallback(() => {
    stop();
    dragging.current = true;
    window.addEventListener("pointermove", updatePointer, { passive: true });
    window.addEventListener("touchmove", updatePointer, { passive: true });
    frame.current = window.requestAnimationFrame(tick);
  }, [stop, tick, updatePointer]);

  useEffect(() => stop, [stop]);

  return { startLessonDndAutoScroll: start, stopLessonDndAutoScroll: stop };
}
